import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { sanitizeReportDocument } from "@/lib/sanitize";
import { buildReportDoc, isFullHtmlDoc, isHtmlContent, markdownToHtml, reportFileName, widenReportDoc } from "@/lib/reportDoc";
import { sortByName, sortByTitle } from "@/lib/sortRows";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import PagedList from "@/components/PagedList";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import MultiSelect from "@/components/MultiSelect";
import RecipientFields, { allRecipients } from "@/components/RecipientFields";
import ScheduleRecipientFields from "@/components/ScheduleRecipientFields";
import { isEligibleInitiativeOwner } from "@/lib/initiativeOwners";
import ScheduleDeliveryHistory from "@/components/ScheduleDeliveryHistory";
import { localPreview } from "@/lib/scheduleTime";
import { SparkleIcon, EnvelopeIcon, PaperPlaneRightIcon, CheckCircleIcon, ClockCounterClockwiseIcon, DownloadSimpleIcon, ArchiveIcon, ArrowUUpLeftIcon, ArrowsOutSimpleIcon, ArrowsInSimpleIcon, CaretDownIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

// "2026-08-31" -> "31 Aug". Split rather than passed to new Date(): a plain
// calendar date parsed as a string is read as UTC and rolls back a day east of
// Greenwich (see lib/localDate.js).
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtShortDay = (iso) => {
  const [, m, d] = String(iso || "").split("-").map(Number);
  if (!m || !d) return String(iso || "");
  return `${d} ${MONTH_SHORT[m - 1]}`;
};

export default function DigestPage() {
  const { user: currentUser } = useAuth();
  // Admins, managers and owners get the whole portfolio (weekly-digest's
  // auto-scoping to "my own projects" applies to plain members only).
  const canSeeWholePortfolio = ["admin", "manager", "owner"].includes(currentUser?.role);
  const [digests, setDigests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  // To / Cc / Bcc rather than one flat list: a digest normally goes TO the
  // owners and copies everyone else.
  const [mail, setMail] = useState({ to: [], cc: [], bcc: [] });
  // Optional note shown above the digest body for this one send only — not
  // saved anywhere, not part of the recurring schedule.
  const [sendMessage, setSendMessage] = useState("");
  const [users, setUsers] = useState([]);
  const [scopedUsers, setScopedUsers] = useState([]);
  const [schedule, setSchedule] = useState(null);
  // Edited locally as the sender changes day/hour/recipients/scope, and only
  // sent to the server on "Save changes" — previously every single field
  // change fired its own network call + toast immediately, which read as
  // unpredictable ("did that just save on its own?") for a form with this
  // many fields. `schedule` above stays the last value actually persisted.
  const [scheduleDraft, setScheduleDraft] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [initiatives, setInitiatives] = useState([]);
  const [projects, setProjects] = useState([]);
  // [{ project_id, user_id }] for every project, so the scope pickers can
  // narrow "who" to the people actually on the chosen work.
  const [memberships, setMemberships] = useState([]);
  // Scope for the NEXT manual generation. Empty arrays mean the whole
  // portfolio, which is what the digest always used to be.
  const [genScope, setGenScope] = useState({ initiative_ids: [], project_ids: [], user_ids: [] });
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scopeTab, setScopeTab] = useState("initiatives");
  // History filters, reading the scope each digest was generated with.
  const [histProject, setHistProject] = useState("all");
  const [histMember, setHistMember] = useState("all");
  const [schedScopeTab, setSchedScopeTab] = useState("initiatives");
  // Personal archive — defaults to hiding what this user has archived.
  const [archiveFilter, setArchiveFilter] = useState("active");
  const [archiving, setArchiving] = useState(false);
  // Collapsible sections — both default open, matching how they always
  // rendered before this could be toggled.
  const [scheduleOpen, setScheduleOpen] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(true);
  // Every attempt run-scheduled-comms evaluated as due for THIS schedule —
  // distinct from schedule.last_run_at/last_status, which only ever holds
  // the single most recent one.
  const [scheduleHistory, setScheduleHistory] = useState([]);

  const loadScheduleHistory = () => {
    api.get("/digest/schedule/history").then(({ data }) => setScheduleHistory(data || [])).catch(() => {});
  };

  // The draft always starts equal to whatever was last persisted — on first
  // load, and again after every successful save (setSchedule below), so
  // "Save changes" only ever re-enables once something is actually edited.
  useEffect(() => { setScheduleDraft(schedule); }, [schedule]);
  const updateDraft = (patch) => setScheduleDraft((d) => ({ ...(d || {}), ...patch }));
  const scheduleDirty = JSON.stringify(scheduleDraft) !== JSON.stringify(schedule);

  const load = async () => {
    // /digest/config is gone: it only ever reported whether RESEND_API_KEY was
    // set, and this app does not send through Resend — outbound mail goes
    // through Microsoft Graph (supabase/functions/_shared/graphMailer.ts).
    /*
      Each request degrades on its own rather than taking the page with it.

      Promise.all rejects as a whole, so one failing call left every piece of
      state unset and the page blank. The schedule in particular now goes
      through an Edge Function that answers 403 to a plain member, which would
      otherwise have blanked the Digest page for them entirely.
    */
    const [d, u, s, i, p, m, t] = await Promise.all([
      api.get("/digest").catch(() => ({ data: [] })),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/digest/schedule").catch(() => ({ data: null })),
      api.get("/initiatives").catch(() => ({ data: [] })),
      api.get("/projects").catch(() => ({ data: [] })),
      api.get("/project-members").catch(() => ({ data: [] })),
      api.get("/team/members").catch(() => null),
    ]);
    setDigests(d.data || []);
    setUsers(sortByName(u.data || []));
    // People in the caller's initiatives — the team-member picker. `users` stays the
    // full roster, for names only.
    setScopedUsers(sortByName((t ? t.data : u.data) || []));
    setSchedule(s.data);
    setInitiatives(sortByTitle(i.data || []));
    setProjects(sortByTitle(p.data || []));
    setMemberships(m.data || []);
    loadScheduleHistory();
    if (!selected && d.data[0]) setSelected(d.data[0]);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/digest/generate", { scope: genScope });
      toast.success("Digest generated");
      setSelected(data);
      /*
        The history list is filtered by what a digest COVERED (digestCovers()),
        against whatever project/member the History filters were left on from
        an earlier session. A digest just generated for a DIFFERENT scope than
        those stale filters would satisfy digestCovers() as false and vanish
        from the list the moment it appeared — reset both to "all" so the
        digest someone just asked for is never hidden by a filter they forgot
        was still set.
      */
      setHistProject("all");
      setHistMember("all");
      const d = await api.get("/digest");
      setDigests(d.data);
      // Open it directly — the same "generate, then open in a new tab"
      // pattern already used for project reports — instead of leaving
      // someone to find it themselves in the history list.
      if (data?.share_token) {
        window.open(`${window.location.origin}/d/${data.share_token}`, "_blank", "noopener,noreferrer");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setGenerating(false); }
  };

  const send = async () => {
    if (!selected) return;
    // Cc/Bcc alone is not a send — somebody has to be the addressee, which is
    // also what the edge function enforces.
    if ((mail.to || []).length === 0) { toast.error("Add at least one To recipient"); return; }
    const everyone = allRecipients(mail);
    setSending(true);
    try {
      await api.post("/digest/send", {
        digest_id: selected._id || selected.id,
        to: mail.to,
        cc: mail.cc,
        bcc: mail.bcc,
        message: sendMessage,
      });
      toast.success(`Sent to ${everyone.length} recipient${everyone.length > 1 ? "s" : ""}: ${everyone.join(", ")}`);
      setMail({ to: [], cc: [], bcc: [] });
      setSendMessage("");
      const d = await api.get("/digest");
      setDigests(d.data);
      setSelected(d.data.find((x) => x._id === selected._id) || selected);
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setSending(false); }
  };

  /*
    Address pools for a digest, derived from the scope the SELECTED digest was
    generated with (metadata.scope).

      To   the owner group + the managers of the projects it covers
      Cc   the owner group + those projects' members
      Bcc  everyone (and Bcc also takes a typed address)

    A portfolio-wide digest names no projects, so To is the owner group alone —
    which is the right answer: there is no single project manager to address.
  */
  const owners = useMemo(() => users.filter(isEligibleInitiativeOwner), [users]);

  const mailPools = useMemo(() => {
    const sc = selected?.metadata?.scope || {};
    const scopedProjects = projects.filter(
      (p) => (sc.project_ids || []).includes(p._id) || (sc.initiative_ids || []).includes(p.initiative_id),
    );
    const projectIds = new Set(scopedProjects.map((p) => p._id));

    const managers = users.filter((u) => scopedProjects.some((p) => p.owner_id === u._id));
    const memberIds = new Set(
      memberships.filter((m) => projectIds.has(m.project_id)).map((m) => m.user_id),
    );
    const projectMembers = users.filter((u) => memberIds.has(u._id));
    const scopedPeople = users.filter((u) => (sc.user_ids || []).includes(u._id));

    const dedupe = (list) => {
      const seen = new Set();
      return list.filter((u) => u && u.email && !seen.has(u._id) && seen.add(u._id));
    };

    return {
      to: dedupe([...owners, ...managers, ...scopedPeople]),
      cc: dedupe([...owners, ...managers, ...projectMembers]),
      bcc: dedupe(scopedUsers),
    };
  }, [selected, projects, users, scopedUsers, memberships, owners]);

  const teamEmails = useMemo(() => scopedUsers.map((u) => u.email).filter(Boolean), [scopedUsers]);

  // Persists the WHOLE draft in one call — the "Save changes" button, not a
  // per-field autosave. Reads scheduleDraft directly rather than taking a
  // patch: everything edited since the last save goes out together.
  const saveSchedule = async () => {
    if (!scheduleDraft) return;
    setScheduleSaving(true);
    try {
      const payload = {
        enabled: !!scheduleDraft.enabled,
        recipients: scheduleDraft.recipients || [],
        // Was missing here entirely — Cc/Bcc typed into the schedule's
        // recipient fields saved happily in local state but never actually
        // reached the server, so they silently reverted to empty on the
        // next save. Fixed alongside this refactor.
        cc: scheduleDraft.cc || [],
        bcc: scheduleDraft.bcc || [],
        day_of_week: scheduleDraft.day_of_week || "sat",
        hour: Number(scheduleDraft.hour ?? 9),
        minute: Number(scheduleDraft.minute ?? 0),
        timezone: scheduleDraft.timezone || "UTC",
        scope_initiative_ids: scheduleDraft.scope_initiative_ids || [],
        scope_project_ids: scheduleDraft.scope_project_ids || [],
        scope_user_ids: scheduleDraft.scope_user_ids || [],
      };
      const { data } = await api.put("/digest/schedule", payload);
      setSchedule(data);
      if (data.warning) {
        // Fixed id so repeated clicks replace the message instead of stacking a
        // toast per tick.
        toast.warning(data.warning, { id: "digest-scope-columns", duration: 8000 });
      } else {
        toast.success("Schedule saved");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setScheduleSaving(false); }
  };

  // Declared ABOVE the digestHtml memo on purpose: that memo reads
  // selectedScopeLabel, and a `const` used before its declaration line is a
  // temporal-dead-zone ReferenceError at render — which blanked this whole
  // page while still compiling cleanly.
  /*
    The three scope lists CASCADE, the way the filters on the Tasks and Projects
    grids already do.

      initiative chosen  -> Projects offers only that initiative's projects,
                            and Team Members only the people on them
      project chosen     -> Team Members offers only that project's people
      nothing chosen     -> everything is on offer

    A person is "on" a project if they own it or hold a project_members row —
    the same set project RLS admits, and the same set a task can be assigned to.
  */
  /*
    Requested directly: a non-admin (manager or member) picking a digest scope
    should only be offered the initiatives/projects/team members they are
    actually part of — never the whole org's list. Before this, the picker's
    base pools were the full `projects`/`initiatives`/`users` fetched for the
    page (projects RLS deliberately grants any manager blanket read access —
    see CLAUDE.md — so this showed literally every project/initiative/person
    in the portfolio, not just the caller's own, even though generating with
    no selection already auto-scopes to just their own work). An admin still
    sees everything, matching the whole-portfolio default they get.

    "Part of" = owns the project, or holds a project_members row on it — the
    same test scopeOptionsFor() below already uses to narrow people once an
    initiative/project is chosen, and the same set project RLS admits.
  */
  const myProjectIds = useMemo(() => {
    if (canSeeWholePortfolio || !currentUser) return null;
    const ids = new Set();
    projects.forEach((p) => {
      if (p.owner_id === currentUser.id) ids.add(p._id);
    });
    memberships.forEach((m) => {
      if (m.user_id === currentUser.id) ids.add(m.project_id);
    });
    return ids;
  }, [canSeeWholePortfolio, currentUser, projects, memberships]);

  const myProjects = myProjectIds ? projects.filter((p) => myProjectIds.has(p._id)) : projects;
  const myInitiativeIds = myProjectIds ? new Set(myProjects.map((p) => p.initiative_id)) : null;
  const myInitiatives = myInitiativeIds ? initiatives.filter((i) => myInitiativeIds.has(i._id)) : initiatives;
  const myPeopleIds = useMemo(() => {
    if (!myProjectIds) return null;
    const ids = new Set(currentUser ? [currentUser.id] : []);
    myProjects.forEach((p) => {
      if (p.owner_id) ids.add(p.owner_id);
      memberships.filter((m) => m.project_id === p._id).forEach((m) => ids.add(m.user_id));
    });
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myProjectIds, memberships, currentUser]);
  const myUsers = myPeopleIds ? users.filter((u) => myPeopleIds.has(u._id)) : scopedUsers;

  const scopeOptionsFor = (scope) => {
    const chosenInitiatives = scope.initiative_ids || [];
    const chosenProjects = scope.project_ids || [];

    const projectPool = chosenInitiatives.length > 0
      ? myProjects.filter((p) => chosenInitiatives.includes(p.initiative_id))
      : myProjects;

    // People are narrowed by the projects actually in play: the explicit
    // selection if there is one, otherwise whatever the initiative choice left.
    const peopleProjects = chosenProjects.length > 0
      ? projectPool.filter((p) => chosenProjects.includes(p._id))
      : projectPool;

    let peoplePool = myUsers;
    if (chosenInitiatives.length > 0 || chosenProjects.length > 0) {
      const ids = new Set();
      peopleProjects.forEach((p) => {
        if (p.owner_id) ids.add(p.owner_id);
        memberships.filter((m) => m.project_id === p._id).forEach((m) => ids.add(m.user_id));
      });
      peoplePool = myUsers.filter((u) => ids.has(u._id));
    }

    return { projectPool, peoplePool };
  };

  const initiativeItems = myInitiatives.map((i) => ({ id: i._id, label: i.title, hint: i.status }));
  // Projects are labelled with their initiative so two similarly named projects
  // under different initiatives are distinguishable in the list.
  const iTitle = Object.fromEntries(initiatives.map((i) => [i._id, i.title]));

  const asPeopleItems = (list) => list.map((u) => ({ id: u._id, label: u.name || u.email, hint: u.role }));
  const asProjectItems = (list) => list.map((p) => ({
    id: p._id,
    label: p.title,
    hint: iTitle[p.initiative_id] || p.status,
  }));

  const genOptions = scopeOptionsFor(genScope);
  const genProjectItems = asProjectItems(genOptions.projectPool);
  const genPeopleItems = asPeopleItems(genOptions.peoplePool);

  const schedOptions = scopeOptionsFor({
    initiative_ids: schedule?.scope_initiative_ids || [],
    project_ids: schedule?.scope_project_ids || [],
  });
  const schedProjectItems = asProjectItems(schedOptions.projectPool);
  const schedPeopleItems = asPeopleItems(schedOptions.peoplePool);

  // "Whole portfolio" is not a fallback for an empty selection — it IS the
  // meaning of an empty selection, both here and in the edge function.
  const scopeSummary = (scope) => {
    const nI = (scope?.initiative_ids || scope?.scope_initiative_ids || []).length;
    const nP = (scope?.project_ids || scope?.scope_project_ids || []).length;
    const nU = (scope?.user_ids || scope?.scope_user_ids || []).length;
    if (nI === 0 && nP === 0 && nU === 0) return "Whole portfolio";
    return [
      nI > 0 ? `${nI} initiative${nI > 1 ? "s" : ""}` : null,
      nP > 0 ? `${nP} project${nP > 1 ? "s" : ""}` : null,
      nU > 0 ? `${nU} team member${nU > 1 ? "s" : ""}` : null,
    ].filter(Boolean).join(" + ");
  };

  // One small tab strip, shared by the generate panel and the schedule panel.
  // Tabs rather than three stacked lists: stacked, the panel grew taller than
  // the viewport and pushed the Generate button off screen.
  const scopeTabs = (active, setActive, counts) => (
    <div className="flex items-center gap-1 mb-2" role="tablist">
      {[
        { k: "initiatives", label: "Initiatives", n: counts.i },
        { k: "projects", label: "Projects", n: counts.p },
        // "Team members", not "People": that is what this app calls them
        // everywhere else (the Team page, Team Roster, By Member).
        { k: "people", label: "Team Members", n: counts.u },
      ].map((t) => (
        <button
          key={t.k}
          type="button"
          role="tab"
          aria-selected={active === t.k}
          onClick={() => setActive(t.k)}
          data-testid={`scope-tab-${t.k}`}
          className={`px-2 py-1 text-[10px] font-mono uppercase tracking-wider rounded-sm border transition-colors ${
            active === t.k
              ? "bg-[#0F172A] text-white border-[#0F172A]"
              : "bg-white text-zinc-600 border-border hover:bg-zinc-50"
          }`}
        >
          {t.label}
          {t.n > 0 && <span className={`ml-1 ${active === t.k ? "text-white/70" : "text-[#0176D3]"}`}>{t.n}</span>}
        </button>
      ))}
    </div>
  );

  /*
    What a saved digest actually covered, straight off the row's metadata.

    Counted from the id arrays rather than trusting the stored label: the
    function writes "the whole portfolio" in prose, and a row written before
    scope existed has no metadata at all. Both must read as "Whole portfolio"
    here, and a scoped digest must never be mistaken for a portfolio-wide one.
  */
  const scopeTextOf = (digest) => {
    const sc = digest?.metadata?.scope;
    const n = (sc?.initiative_ids?.length || 0) + (sc?.project_ids?.length || 0) + (sc?.user_ids?.length || 0);
    if (n === 0) return "Whole portfolio";
    const label = sc?.label || "";
    // Strip the function's leading article so the label reads as a heading.
    return label.replace(/^the\s+/i, "") || "Scoped";
  };

  /*
    The kind of thing a digest covers, for the first line of a history row.

    The Reports history reads scope / subject / date, and this now matches it:
    a report's first line is its scope_type ("project", "member"), so a digest's
    is the equivalent word derived from the ids it was scoped to.
  */
  const scopeKindOf = (digest) => {
    const sc = digest?.metadata?.scope;
    const nI = (sc?.initiative_ids || []).length;
    const nP = (sc?.project_ids || []).length;
    const nU = (sc?.user_ids || []).length;
    const kinds = (nI > 0 ? 1 : 0) + (nP > 0 ? 1 : 0) + (nU > 0 ? 1 : 0);
    if (kinds === 0) return "portfolio";
    if (kinds > 1) return "mixed scope";
    if (nI > 0) return nI > 1 ? "initiatives" : "initiative";
    if (nP > 0) return nP > 1 ? "projects" : "project";
    return nU > 1 ? "team members" : "team member";
  };

  // Which week the digest is ABOUT, which is the useful identifier — five
  // digests generated on the same afternoon all showed the same date and were
  // indistinguishable in the list.
  const weekTextOf = (digest) => {
    const start = digest?.metadata?.week_start;
    const end = digest?.metadata?.week_end;
    if (!start) return null;
    return end ? `${fmtShortDay(start)}–${fmtShortDay(end)}` : `From ${fmtShortDay(start)}`;
  };

  /*
    Filtering the history by what a digest COVERED.

    A digest's scope is stored on the row (metadata.scope), so a project filter
    matches a digest scoped to that project — or to an initiative that contains
    it, since choosing an initiative pulls its projects in and a reader looking
    for "digests about this project" means those too.

    Rows written before scope existed have no metadata and are portfolio-wide,
    so they only appear under "All".
  */
  const digestCovers = (digest, projectId, memberId) => {
    const sc = digest?.metadata?.scope;
    if (projectId !== "all") {
      if ((sc?.project_ids || []).includes(projectId)) return true;
      const proj = projects.find((p) => p._id === projectId);
      return Boolean(proj && (sc?.initiative_ids || []).includes(proj.initiative_id));
    }
    if (memberId !== "all") return (sc?.user_ids || []).includes(memberId);
    return true;
  };

  const filteredDigests = digests.filter((d) => {
    // "Active" and "All" both hide archived digests — only the explicit
    // "Archived" option reveals them, so an archived digest is never a
    // surprise sitting in the default/all view.
    if (archiveFilter !== "archived" && d.archived) return false;
    if (archiveFilter === "archived" && !d.archived) return false;
    return digestCovers(d, histProject, histMember);
  });

  const toggleArchive = async (digest) => {
    if (!digest) return;
    const id = digest._id || digest.id;
    setArchiving(true);
    try {
      if (digest.archived) {
        await api.delete(`/digest/${id}/archive`);
        toast.success("Digest unarchived");
      } else {
        await api.post(`/digest/${id}/archive`, {});
        toast.success("Digest archived");
      }
      const { data: fresh } = await api.get("/digest");
      setDigests(fresh);
      const stillThere = fresh.find((d) => (d._id || d.id) === id);
      setSelected(stillThere || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setArchiving(false);
    }
  };

  const selectedScopeLabel = scopeTextOf(selected);
  const selectedSentTo = (selected?.recipients || []);

  /*
    The digest is rendered through the SAME document template as Reports —
    lib/reportDoc.js's buildReportDoc + markdownToHtml, sanitized and shown in
    an iframe — instead of the ad-hoc markdown-to-div renderer this page used to
    carry. One template means a digest and a report read as the same publication,
    and the preview is byte-for-byte what the download button hands over.
  */
  const digestHtml = useMemo(() => {
    if (!selected) return "";
    const content = selected.markdown || "";

    /*
      Same three-way branch as ReportsPage, so a digest and a report are the
      same publication with different content:
        * a complete document -> passed through,
        * an HTML body fragment (what the digest prompt now emits) -> wrapped,
        * markdown (every digest row written before that) -> converted, then
          wrapped, so history picks up the design instead of staying plain.
    */
    if (isFullHtmlDoc(content)) return sanitizeReportDocument(content);
    const bodyHtml = isHtmlContent(content) ? content : markdownToHtml(content);

    const generatedAt = selected.created_at ? new Date(selected.created_at) : null;
    return sanitizeReportDocument(
      buildReportDoc({
        title: "Weekly Digest",
        eyebrow: "NG-IT Initiative Board",
        subtitle: "Executive summary for the week",
        meta: [
          { label: "Period", value: weekTextOf(selected) || "—" },
          { label: "Scope", value: selectedScopeLabel },
          {
            label: "Generated",
            value: generatedAt && !Number.isNaN(generatedAt.getTime()) ? generatedAt.toLocaleString() : "—",
          },
          { label: "Sent", value: selected.sent_at ? new Date(selected.sent_at).toLocaleString() : "Not sent" },
        ],
        bodyHtml,
        footer:
          "Source: NG-IT Initiative Board — generated from last week's completions, active projects, " +
          "blocked tasks and upcoming leaves. Figures reflect the board at generation time.",
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, selectedScopeLabel]);

  const download = () => {
    if (!digestHtml) return;
    try {
      const blob = new Blob([digestHtml], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = reportFileName("Weekly Digest", selected?.created_at ? new Date(selected.created_at) : new Date());
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not build the download"); }
  };


  // Same sandbox as the Reports preview: no scripts, no same-origin.
  const IFRAME_SANDBOX = "allow-popups allow-popups-to-escape-sandbox";

  const DAYS = [
    { v: "mon", label: "Monday" }, { v: "tue", label: "Tuesday" }, { v: "wed", label: "Wednesday" },
    { v: "thu", label: "Thursday" }, { v: "fri", label: "Friday" }, { v: "sat", label: "Saturday" }, { v: "sun", label: "Sunday" },
  ];

  return (
    <AppLayout>
      <div className="trk-sticky-header sticky top-0 z-20 px-8 py-4 flex items-center justify-between">
        <div>
          <div className="trk-uppercase-label">Comms</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Weekly AI Digest</h1>
        </div>
        <div className="text-xs font-mono text-zinc-500">
          Mail sent from the organization&apos;s Microsoft 365 mailbox
        </div>
      </div>

      <div className="px-8 py-6 grid lg:grid-cols-[360px_1fr] gap-6 trk-fade-in">
        {/* Left: generator + history */}
        <div className="space-y-4">
          <div className="border border-border rounded-sm p-5">
            <div className="trk-uppercase-label mb-1">Generate</div>
            <h3 className="font-display text-lg font-bold mb-2">Monday morning digest</h3>
            <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
              Claude Sonnet 4.5 writes an in-depth report on the last seven days: what reached Live or Closed,
              which projects moved, what is overdue or on hold, ETA slippage, and who is away.
            </p>
            {/* Scope. Collapsed by default: the common case is the whole
                portfolio, and an always-open pair of long lists would bury the
                Generate button. */}
            <div className="mb-3">
              <button
                type="button"
                onClick={() => setScopeOpen((v) => !v)}
                data-testid="digest-scope-toggle"
                className="w-full flex items-center justify-between text-left border border-border rounded-sm px-3 py-2 hover:bg-zinc-50 transition-colors"
              >
                <span className="trk-uppercase-label">Scope</span>
                <span className="text-xs text-zinc-600">{scopeSummary(genScope)}</span>
              </button>

              {scopeOpen && (
                <div className="border border-t-0 border-border rounded-b-sm p-3">
                  {scopeTabs(scopeTab, setScopeTab, {
                    i: genScope.initiative_ids.length,
                    p: genScope.project_ids.length,
                    u: (genScope.user_ids || []).length,
                  })}

                  {scopeTab === "initiatives" && (
                    <>
                      <MultiSelect
                        items={initiativeItems}
                        value={genScope.initiative_ids}
                        onChange={(ids) => setGenScope({ ...genScope, initiative_ids: ids })}
                        placeholder="Search initiatives…"
                        testId="digest-scope-initiatives"
                      />
                      <div className="text-[10px] font-mono text-zinc-400 mt-1">
                        Picking an initiative includes every project under it.
                      </div>
                    </>
                  )}
                  {scopeTab === "projects" && (
                    <>
                      <MultiSelect
                        items={genProjectItems}
                        value={genScope.project_ids}
                        onChange={(ids) => setGenScope({ ...genScope, project_ids: ids })}
                        placeholder="Search projects…"
                        testId="digest-scope-projects"
                      />
                      {genScope.initiative_ids.length > 0 && (
                        <div className="text-[10px] font-mono text-zinc-400 mt-1">
                          Showing only projects under the selected initiative
                          {genScope.initiative_ids.length > 1 ? "s" : ""}.
                        </div>
                      )}
                    </>
                  )}
                  {scopeTab === "people" && (
                    <>
                      <MultiSelect
                        items={genPeopleItems}
                        value={genScope.user_ids || []}
                        onChange={(ids) => setGenScope({ ...genScope, user_ids: ids })}
                        placeholder="Search team members…"
                        emptyMessage="Nobody is on the selected work yet."
                        testId="digest-scope-people"
                      />
                      <div className="text-[10px] font-mono text-zinc-400 mt-1">
                        {genScope.initiative_ids.length > 0 || genScope.project_ids.length > 0
                          ? "Showing only people on the selected work."
                          : "Narrows the digest to these team members' tasks and leave."}
                      </div>
                    </>
                  )}

                  <div className="text-[10px] font-mono text-zinc-500 mt-2 leading-tight">
                    Nothing selected = every initiative and project.
                  </div>
                  {(genScope.initiative_ids.length > 0 || genScope.project_ids.length > 0 || (genScope.user_ids || []).length > 0) && (
                    <button
                      type="button"
                      onClick={() => setGenScope({ initiative_ids: [], project_ids: [], user_ids: [] })}
                      data-testid="digest-scope-clear"
                      className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-zinc-900 mt-1"
                    >
                      Clear — cover everything
                    </button>
                  )}
                </div>
              )}
            </div>

            <Button
              onClick={generate}
              disabled={generating}
              data-testid="generate-digest-button"
              className="w-full rounded-sm bg-[#0A0A0A] hover:bg-black text-white"
            >
              <SparkleIcon size={14} className="mr-1" weight="fill" />
              {generating ? "Writing…" : "Generate this week's digest"}
            </Button>
            {/* States the two things a reader would otherwise have to guess:
                which period the digest covers, and that an untouched Scope
                control means everything. */}
            <div className="text-[10px] font-mono text-zinc-500 mt-2 leading-tight">
              Covers the last 7 days · {scopeSummary(genScope)}
            </div>
          </div>

          {/* Auto-Schedule */}
          {schedule && (
            <div className="border border-border rounded-sm p-5" data-testid="digest-schedule-panel">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="trk-uppercase-label mb-1 flex items-center gap-1">
                    <ClockCounterClockwiseIcon size={12} /> Auto-schedule
                  </div>
                  <h3 className="font-display text-base font-bold">Weekly delivery</h3>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!!scheduleDraft?.enabled}
                    disabled={scheduleSaving}
                    onCheckedChange={(v) => updateDraft({ enabled: v })}
                    data-testid="schedule-enabled-switch"
                  />
                  <button
                    type="button"
                    onClick={() => setScheduleOpen((v) => !v)}
                    data-testid="digest-schedule-toggle"
                    className="p-1 rounded-sm hover:bg-zinc-100 text-zinc-500"
                    aria-label={scheduleOpen ? "Collapse" : "Expand"}
                  >
                    <CaretDownIcon size={14} className={`transition-transform ${scheduleOpen ? "" : "-rotate-90"}`} />
                  </button>
                </div>
              </div>

              {scheduleOpen && scheduleDraft && (
              <>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div>
                  <Label className="trk-uppercase-label mb-1 block">Day</Label>
                  <Select value={scheduleDraft.day_of_week} onValueChange={(v) => updateDraft({ day_of_week: v })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="schedule-day-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DAYS.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="trk-uppercase-label mb-1 block">Hour (UTC)</Label>
                  <Select value={String(scheduleDraft.hour)} onValueChange={(v) => updateDraft({ hour: Number(v) })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="schedule-hour-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }).map((_, h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="trk-uppercase-label mb-1 block">Minute (UTC)</Label>
                  {/* IST (and other half-hour-offset zones) cannot land on a
                      whole UTC hour — this is how "7:00 PM IST" becomes
                      possible to actually select (13:30 UTC). */}
                  <Select value={String(scheduleDraft.minute ?? 0)} onValueChange={(v) => updateDraft({ minute: Number(v) })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="schedule-minute-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[0, 15, 30, 45].map((m) => <SelectItem key={m} value={String(m)}>:{String(m).padStart(2, "0")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* pg_cron (and run-scheduled-comms) evaluate entirely in UTC —
                  this is the one place that UTC time is translated into
                  whatever timezone the person reading it is actually in, so
                  "why didn't it send at 7 PM" has an answer before saving,
                  not after. */}
              <div className="text-[10px] font-mono text-zinc-500 mb-3 -mt-1">
                Day/hour/minute above are UTC. Next run lands at{" "}
                <span className="font-semibold text-zinc-700">
                  {localPreview(scheduleDraft.day_of_week, scheduleDraft.hour, scheduleDraft.minute ?? 0)}
                </span>{" "}
                in your own time zone.
              </div>

              <ScheduleRecipientFields
                value={scheduleDraft}
                onChange={updateDraft}
                testId="schedule"
                teamEmails={teamEmails}
              />

              <Label className="trk-uppercase-label mb-1 block">Scheduled scope</Label>
              <div className="border border-border rounded-sm p-2 mb-3">
                <div className="text-[10px] font-mono text-zinc-500 mb-2">{scopeSummary(scheduleDraft)}</div>
                {scopeTabs(schedScopeTab, setSchedScopeTab, {
                  i: (scheduleDraft.scope_initiative_ids || []).length,
                  p: (scheduleDraft.scope_project_ids || []).length,
                  u: (scheduleDraft.scope_user_ids || []).length,
                })}
                {schedScopeTab === "initiatives" && (
                  <MultiSelect
                    items={initiativeItems}
                    value={scheduleDraft.scope_initiative_ids || []}
                    onChange={(ids) => updateDraft({ scope_initiative_ids: ids })}
                    placeholder="Search initiatives…"
                    testId="schedule-scope-initiatives"
                    maxHeight="max-h-28"
                  />
                )}
                {schedScopeTab === "projects" && (
                  <MultiSelect
                    items={schedProjectItems}
                    value={scheduleDraft.scope_project_ids || []}
                    onChange={(ids) => updateDraft({ scope_project_ids: ids })}
                    placeholder="Search projects…"
                    testId="schedule-scope-projects"
                    maxHeight="max-h-28"
                  />
                )}
                {schedScopeTab === "people" && (
                  <MultiSelect
                    items={schedPeopleItems}
                    value={scheduleDraft.scope_user_ids || []}
                    onChange={(ids) => updateDraft({ scope_user_ids: ids })}
                    placeholder="Search team members…"
                    testId="schedule-scope-people"
                    maxHeight="max-h-28"
                  />
                )}
              </div>

              <Button
                onClick={saveSchedule}
                disabled={!scheduleDirty || scheduleSaving}
                data-testid="schedule-save-button"
                className="w-full rounded-sm bg-[#0176D3] hover:bg-[#005FB2] text-white disabled:opacity-50 mb-3"
              >
                {scheduleSaving ? "Saving…" : scheduleDirty ? "Save changes" : "Saved"}
              </Button>

              {/* This describes what's actually SAVED (schedule), never the
                  unsaved draft above — so it stays trustworthy even mid-edit. */}
              <div className="text-[10px] font-mono text-zinc-500 leading-tight">
                {schedule.enabled
                  ? `Saved: runs ${DAYS.find((d) => d.v === schedule.day_of_week)?.label} at ${String(schedule.hour).padStart(2, "0")}:${String(schedule.minute).padStart(2, "0")} UTC (${localPreview(schedule.day_of_week, schedule.hour, schedule.minute)})`
                  : "Saved: auto-send disabled"}
                {scheduleDirty && <div className="text-amber-600 mt-1">Unsaved changes.</div>}
                {schedule.last_run_at && (
                  <div className="text-zinc-400 mt-1">Last: {new Date(schedule.last_run_at).toLocaleString()} — {schedule.last_status}</div>
                )}
              </div>

              <div className="mt-3">
                <Label className="trk-uppercase-label mb-1 block">Weekly delivery history</Label>
                <div className="border border-border rounded-sm px-2">
                  <ScheduleDeliveryHistory rows={scheduleHistory} testId="digest-schedule-history" />
                </div>
              </div>
              </>
              )}
            </div>
          )}

          <div className="border border-border rounded-sm">
            <div className={historyOpen ? "p-3 border-b border-border" : "p-3"}>
              <button
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
                data-testid="digest-history-toggle"
                className="w-full flex items-center justify-between trk-uppercase-label mb-2"
              >
                <span>History ({filteredDigests.length}{filteredDigests.length !== digests.length ? ` of ${digests.length}` : ""})</span>
                <CaretDownIcon size={12} className={`transition-transform text-zinc-400 ${historyOpen ? "" : "-rotate-90"}`} />
              </button>
              {historyOpen && (
              <div className="space-y-2">
                <div>
                  <Label className="trk-uppercase-label text-[10px] mb-1 block">Show</Label>
                  <Select value={archiveFilter} onValueChange={setArchiveFilter}>
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="digest-archive-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                      <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="trk-uppercase-label text-[10px] mb-1 block">By project</Label>
                  <Select
                    value={histProject}
                    onValueChange={(v) => { setHistProject(v); if (v !== "all") setHistMember("all"); }}
                  >
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="digest-history-project">
                      <SelectValue placeholder="All projects" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All projects</SelectItem>
                      {projects.map((p) => <SelectItem key={p._id} value={p._id}>{p.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="trk-uppercase-label text-[10px] mb-1 block">By team member</Label>
                  <Select
                    value={histMember}
                    onValueChange={(v) => { setHistMember(v); if (v !== "all") setHistProject("all"); }}
                  >
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="digest-history-member">
                      <SelectValue placeholder="All team members" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All team members</SelectItem>
                      {scopedUsers.map((u) => <SelectItem key={u._id} value={u._id}>{u.name || u.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {(histProject !== "all" || histMember !== "all") && (
                  <button
                    type="button"
                    onClick={() => { setHistProject("all"); setHistMember("all"); }}
                    data-testid="digest-history-clear"
                    className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-zinc-900"
                  >
                    Clear filters
                  </button>
                )}
              </div>
              )}
            </div>
            {historyOpen && (
            <div className="max-h-[55vh] overflow-y-auto">
              <PagedList items={filteredDigests} pageSize={15} testIdPrefix="digests" className="px-3 pb-2">
                {(rows) => (
                  <div className="-mx-3">
              {rows.map((d) => (
                <button
                  key={d._id}
                  onClick={() => setSelected(d)}
                  data-testid={`digest-item-${d._id}`}
                  className={`w-full text-left px-3 py-3 border-b border-border last:border-0 hover:bg-zinc-50 ${selected?._id === d._id ? "bg-[#EFF6FF] border-l-2 border-l-[#0176D3]" : ""}`}
                >
                  {/* Same shape as a Reports history row — kind, then what it
                      covers, with the date on the right. The period and sent
                      state follow underneath, which a report does not need but a
                      digest does: two digests of the same scope differ only by
                      the week they cover. */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                        {scopeKindOf(d)}
                        {d.archived && <ArchiveIcon size={11} className="text-zinc-400" />}
                      </div>
                      <div className="font-medium text-sm truncate" title={scopeTextOf(d)}>
                        {scopeTextOf(d)}
                      </div>
                      <div className="text-[10px] font-mono text-zinc-400 mt-0.5 truncate">
                        {weekTextOf(d) || "period not recorded"}
                        {" · "}
                        {/* d.recipients, not d.sent_to: the shim exposes
                            delivered_to as `recipients`, so the old field name
                            read undefined and every sent digest claimed 0. */}
                        {d.sent_at ? `sent to ${(d.recipients || []).length}` : "draft"}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-[10px] text-zinc-400 font-mono">
                        {new Date(d.created_at).toLocaleDateString()}
                      </span>
                      {d.sent_at && <CheckCircleIcon size={14} weight="fill" className="text-emerald-500" />}
                    </div>
                  </div>
                </button>
              ))}
                  </div>
                )}
              </PagedList>
              {digests.length === 0 && <div className="p-6 text-sm text-zinc-500">No digests yet.</div>}
              {digests.length > 0 && filteredDigests.length === 0 && (
                <div className="p-6 text-sm text-zinc-500" data-testid="digest-history-no-match">
                  No digest covers that. Only digests generated with a matching scope appear here —
                  portfolio-wide ones show under &ldquo;All&rdquo;.
                </div>
              )}
            </div>
            )}
          </div>
        </div>

        {/* Right: preview + send */}
        <div className="border border-border rounded-sm min-h-[70vh] flex flex-col">
          {selected ? (
            <>
              <div className="p-5 border-b border-border flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="trk-uppercase-label">Preview</div>
                  <div className="text-xs font-mono text-zinc-500 mt-1">
                    Generated {selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => toggleArchive(selected)}
                    disabled={archiving}
                    className="rounded-sm h-9"
                    data-testid="digest-archive-button"
                  >
                    {selected?.archived
                      ? <><ArrowUUpLeftIcon size={14} className="mr-1" /> Unarchive</>
                      : <><ArchiveIcon size={14} className="mr-1" /> Archive</>}
                  </Button>
                  <Button variant="outline" onClick={download} className="rounded-sm h-9" data-testid="digest-download">
                    <DownloadSimpleIcon size={14} className="mr-1" /> Download
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setExpanded(true)}
                    disabled={!digestHtml}
                    className="rounded-sm h-9"
                    data-testid="digest-expand-button"
                  >
                    <ArrowsOutSimpleIcon size={14} className="mr-1" /> Expand
                  </Button>
                </div>
              </div>
              {/* An empty digest means generation failed upstream (no Anthropic
                  key, a rate limit) and the old edge function persisted the
                  empty string anyway. Say so instead of rendering a blank
                  document that looks like the report template is broken. */}
              {!(selected.markdown || "").trim() ? (
                <div className="flex-1 min-h-[60vh] bg-[#EEF1F0] p-8" data-testid="digest-empty-body">
                  <div className="max-w-lg mx-auto bg-white border border-amber-200 rounded-sm p-5">
                    <div className="trk-uppercase-label text-amber-700 mb-1">Empty digest</div>
                    <p className="text-sm text-zinc-700 leading-relaxed">
                      This digest was saved with no content, which means the AI call failed when it
                      was generated — almost always a missing Anthropic API key.
                    </p>
                    <p className="text-sm text-zinc-700 leading-relaxed mt-2">
                      Add a personal key under <span className="font-medium">Account settings</span>, or
                      set the org-wide <code className="font-mono text-xs">ANTHROPIC_API_KEY</code> secret
                      on the Supabase project, then generate again.
                    </p>
                  </div>
                </div>
              ) : (
                <iframe
                  title="Digest preview"
                  sandbox={IFRAME_SANDBOX}
                  srcDoc={digestHtml}
                  data-testid="digest-preview-frame"
                  className="w-full flex-1 min-h-[60vh] border-0 bg-[#EEF1F0]"
                />
              )}

              {/* Send controls */}
              <div className="border-t border-border p-5 bg-zinc-50/40" data-testid="digest-send-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="trk-uppercase-label">Send email</div>
                  {selected.sent_at && (
                    <div className="text-[10px] font-mono text-emerald-700 flex items-center gap-1">
                      <CheckCircleIcon size={12} weight="fill" /> Sent · {new Date(selected.sent_at).toLocaleString()}
                    </div>
                  )}
                </div>

                {/* Who this digest already went to, read off digests.delivered_to.
                    Without this the page cleared the recipient chips on success
                    and left no record on screen of where it had been sent. */}
                {selectedSentTo.length > 0 && (
                  <div className="mb-3 border border-emerald-200 bg-emerald-50 rounded-sm px-3 py-2" data-testid="digest-sent-to">
                    <div className="trk-uppercase-label text-emerald-800 mb-1">
                      Delivered to {selectedSentTo.length}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {selectedSentTo.map((r) => (
                        <span key={r} className="text-[11px] font-mono bg-white border border-emerald-200 rounded-sm px-1.5 py-0.5 text-emerald-900">{r}</span>
                      ))}
                    </div>
                  </div>
                )}
                <RecipientFields
                  value={mail}
                  onChange={setMail}
                  pools={mailPools}
                  testId="digest-recipients"
                />
                <div className="mt-3">
                  <Label className="trk-uppercase-label mb-1 block">Message (optional)</Label>
                  <p className="text-xs text-muted-foreground mb-1.5">
                    The email always opens with this fixed greeting — your note is added as one more
                    paragraph right after it, before the digest body:
                    <br />
                    <span className="italic">
                      "Dear All, Pls refer the below status update for {scopeTextOf(selected)}
                      {weekTextOf(selected) ? ` (covering ${weekTextOf(selected)})` : ""}."
                    </span>
                  </p>
                  <Textarea
                    value={sendMessage}
                    onChange={(e) => setSendMessage(e.target.value)}
                    placeholder="Add a note to appear above the digest in the email…"
                    rows={3}
                    className="rounded-sm"
                    data-testid="digest-send-message"
                  />
                </div>
                <Button
                  onClick={send}
                  disabled={sending || (mail.to || []).length === 0}
                  data-testid="digest-send-button"
                  className="w-full mt-3 rounded-sm bg-[#0176D3] hover:bg-[#005FB2] text-white disabled:opacity-50"
                >
                  <PaperPlaneRightIcon size={14} className="mr-1" weight="fill" />
                  {sending ? "Sending…" : `Send to ${allRecipients(mail).length || 0}`}
                </Button>
              </div>
            </>
          ) : (
            <div className="p-10 text-sm text-zinc-500">
              <EnvelopeIcon size={28} className="text-zinc-300 mb-3" />
              Generate a digest to preview and send it.
            </div>
          )}
        </div>
      </div>

      {/* Expanded digest view — same pattern as the Reports page's Expand. */}
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent
          data-testid="digest-expanded-dialog"
          className="max-w-none w-[96vw] h-[94vh] p-0 gap-0 rounded-sm flex flex-col sm:rounded-sm"
        >
          {/* pr-14 leaves room for DialogContent's own absolute close X at
              right-4, which would otherwise sit on top of Collapse. */}
          <div className="pl-5 pr-14 py-4 border-b border-border flex items-start justify-between gap-4 flex-wrap shrink-0">
            <div className="min-w-0">
              <div className="trk-uppercase-label">{selected ? scopeKindOf(selected) : ""} digest — expanded</div>
              <DialogTitle className="font-display text-xl font-extrabold tracking-tight truncate">
                {selected ? scopeTextOf(selected) : ""}
              </DialogTitle>
              <div className="text-xs text-zinc-500 mt-1 font-mono">
                {selected && (weekTextOf(selected) || `Generated ${selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"}`)}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => toggleArchive(selected)}
                disabled={archiving || !selected}
                data-testid="digest-archive-button-expanded"
                className="rounded-sm"
              >
                {selected?.archived
                  ? <><ArrowUUpLeftIcon size={14} className="mr-1.5" /> Unarchive</>
                  : <><ArchiveIcon size={14} className="mr-1.5" /> Archive</>}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={download}
                disabled={!digestHtml}
                data-testid="digest-download-button-expanded"
                className="rounded-sm"
              >
                <DownloadSimpleIcon size={14} className="mr-1.5" /> Download
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExpanded(false)}
                data-testid="digest-collapse-button"
                className="rounded-sm"
              >
                <ArrowsInSimpleIcon size={14} className="mr-1.5" /> Collapse
              </Button>
            </div>
          </div>
          <iframe
            title="Digest expanded"
            sandbox={IFRAME_SANDBOX}
            srcDoc={widenReportDoc(digestHtml)}
            data-testid="digest-expanded-frame"
            className="w-full flex-1 border-0 bg-[#EEF1F0]"
          />
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
